import { addSvgIconsToDocument } from '@dixa/lib-shared-habla';
import { PureComponent } from 'react';
import ReactDOM from 'react-dom';

type ComposeWindowPortalProps = {
  url: string;
  name: string;
  title: string;
  features: any;
  onBlock?: () => void;
  onOpen?: (window: any) => void;
  onUnload?: () => void;
  center: 'parent' | 'screen';
  copyStyles: boolean;
  children: any;
};

type StateT = {
  mounted: boolean;
};

class ComposeWindowPortal extends PureComponent<
  ComposeWindowPortalProps,
  StateT
> {
  static defaultProps = {
    url: '',
    name: '',
    title: '',
    features: { width: '600px', height: '640px' },
    onBlock: null,
    onOpen: null,
    onUnload: null,
    center: 'parent',
    copyStyles: false,
  };

  container: HTMLDivElement;
  window: any;
  windowCheckerInterval?: number;
  released: boolean;

  constructor(props: ComposeWindowPortalProps) {
    super(props);
    this.container = document.createElement('div');
    this.window = null;
    this.released = false;
    this.state = {
      mounted: false,
    };
  }

  render() {
    if (!this.state.mounted) return null;
    return ReactDOM.createPortal(this.props.children, this.container);
  }

  componentDidMount() {
    this.openChild();
    this.setState({ mounted: true });
  }

  /** Create the new window when NewWindow component mount. */
  openChild() {
    const { url, title, name, features, onBlock, onOpen, center } = this.props;

    // Prepare position of the new window to be centered against the 'parent' window or 'screen'.
    if (
      typeof center === 'string' &&
      (features.width === undefined || features.height === undefined)
    ) {
      console.warn(
        'width and height window features must be present when a center prop is provided',
      );
    } else if (center === 'parent' && window.top) {
      features.left =
        window.top.outerWidth / 2 + window.top.screenX - features.width / 2;
      features.top =
        window.top.outerHeight / 2 + window.top.screenY - features.height / 2;
    } else if (center === 'screen') {
      const screenLeft =
        window.screenLeft !== undefined
          ? window.screenLeft
          : (window.screen as any).left;
      const screenTop =
        window.screenTop !== undefined
          ? window.screenTop
          : (window.screen as any).top;

      const width =
        window.innerWidth ||
        (document.documentElement && document.documentElement.clientWidth) ||
        window.screen.width;
      const height =
        window.innerHeight ||
        (document.documentElement && document.documentElement.clientHeight) ||
        window.screen.height;

      features.left = width / 2 - features.width / 2 + screenLeft;
      features.top = height / 2 - features.height / 2 + screenTop;
    }

    // Open a new window.
    this.window = window.open(url, name, toWindowFeatures(features));

    // When a new window use content from a cross-origin there's no way we can attach event
    // to it. Therefore, we need to detect in a interval when the new window was destroyed
    // or was closed.
    this.windowCheckerInterval = window.setInterval(() => {
      if (!this.window || this.window.closed) {
        this.release();
      }
    }, 50);

    // Check if the new window was succesfully opened.
    try {
      if (this.window && this.window.document) {
        this.window.document.title = title;
        this.window.document.body.appendChild(this.container);

        // If specified, copy styles from parent window's document.
        if (this.props.copyStyles) {
          if (this.window) {
            copyStyles(document, this.window.document);
          }
        }

        if (typeof onOpen === 'function') {
          onOpen(this.window);
        }

        // Release anything bound to this component before the new window unload.
        if (this.window) {
          this.window.addEventListener('beforeunload', () => this.release());
        }
      } else {
        // Handle error on opening of new window.
        if (typeof onBlock === 'function') {
          onBlock();
        } else {
          console.warn(
            'A new window could not be opened. Maybe it was blocked.',
          );
        }
      }
    } catch (error) {
      // do nothing
      console.error(error);
    }
  }

  componentWillUnmount() {
    if (this.window) {
      this.window.close();
    }
  }

  release() {
    // This method can be called only once.
    if (this.released) {
      return;
    }
    this.released = true;

    // Remove checker interval.
    window.clearInterval(this.windowCheckerInterval);

    // Call any function bound to the `onUnload` prop.
    const { onUnload } = this.props;

    if (typeof onUnload === 'function') {
      onUnload();
    }
  }
}

function copyStyles(source: Document, target: Document) {
  addSvgIconsToDocument(target);

  // Copy color mode attribute from parent to popup window
  const colorMode = source.documentElement.getAttribute('data-color-mode');
  if (colorMode) {
    target.documentElement.setAttribute('data-color-mode', colorMode);
  }

  // Copy any classes from body element that might affect styling
  if (source.body.className) {
    target.body.className = source.body.className;
  }

  Array.from(source.styleSheets).forEach((styleSheet: CSSStyleSheet) => {
    // For <style> elements
    let rules;
    try {
      rules = styleSheet.cssRules;
    } catch (err) {
      // console.error(err)
    }
    if (rules) {
      const newStyleEl = source.createElement('style');

      if (styleSheet.cssRules) {
        // Write the text of each rule into the body of the style element
        Array.from(styleSheet.cssRules).forEach((cssRule: CSSRule) => {
          const { cssText, type } = cssRule;
          let returnText = cssText;
          // Check if the cssRule type is CSSImportRule (3) or CSSFontFaceRule (5) to handle local imports on a about:blank page
          // '/custom.css' turns to 'http://my-site.com/custom.css'
          if ([3, 5].includes(type)) {
            returnText = cssText
              .split('url(')
              .map((line: any) => {
                if (line[1] === '/') {
                  return `${line.slice(0, 1)}${
                    window.location.origin
                  }${line.slice(1)}`;
                }
                return line;
              })
              .join('url(');
          }
          newStyleEl.appendChild(source.createTextNode(returnText));
        });

        if (target.head) {
          target.head.appendChild(newStyleEl);
        }
      } else if (styleSheet.href) {
        // for <link> elements loading CSS from a URL
        const newLinkEl = source.createElement('link');
        newLinkEl.rel = 'stylesheet';
        newLinkEl.href = styleSheet.href;
        if (target.head) {
          target.head.appendChild(newLinkEl);
        }
      }
    }
  });
}

function toWindowFeatures(obj: any) {
  return Object.keys(obj)
    .reduce((features: Array<string>, name: string) => {
      const value = obj[name];
      if (typeof value === 'boolean') {
        features.push(`${name}=${value ? 'yes' : 'no'}`);
      } else {
        features.push(`${name}=${value}`);
      }
      return features;
    }, [])
    .join(',');
}

export default ComposeWindowPortal;
